import { useState, useEffect } from 'react';
import { Download, Printer, Settings, Trash2, Eye, FileText } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Dropdown } from '../../components/ui/Dropdown';
import { Table } from '../../components/ui/Table';
import { ReportConfigModal } from '../../components/reports/ReportConfigModal';
import { DocumentPreview } from '../../components/reports/preview/DocumentPreview';
import { ReportHeader } from '../../components/reports/ReportHeader';
import { PrintableReport } from '../../components/reports/PrintableReport';
import { useReports } from '../../hooks/useReports';
import { useSavedReports } from '../../hooks/useSavedReports';
import { useFinancialExport } from '../../hooks/useFinancialExport';
import { useOrganization } from '../../hooks/useOrganization';
import { useDashboard } from '../../hooks/useDashboard';
import { useAuth0 } from '@auth0/auth0-react';
import type { FinancialStatementType } from '../../types/reports';
import type { GeneratedReport } from '../../services/storage/ReportsStorageService';

const STATEMENT_TYPES: Record<FinancialStatementType, string> = {
  'balance': 'Bilan',
  'income': 'Compte de Résultat',
  'cashflow': 'Tableau des Flux de Trésorerie',
  'equity-changes': 'Tableau de Variation des Capitaux Propres',
  'notes': 'Notes Annexes',
  'reconciliation': 'État de Rapprochement',
  'analytical': 'État Analytique',
  'social': 'Bilan Social',
  'statistics': 'État Statistique'
};

export function ReportsPage() {
  const { user } = useAuth0();
  const { organization: orgData } = useOrganization();
  const { dashboardData, loading: dashboardLoading } = useDashboard();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<GeneratedReport | null>(null);
  const [currentConfig, setCurrentConfig] = useState<{
    type: FinancialStatementType;
    format: 'pdf' | 'excel';
    currency: 'USD' | 'CDF';
  }>({
    type: 'balance',
    format: 'pdf',
    currency: 'CDF'
  });

  const { data, loading, error, generateReport } = useReports();
  const { savedReports, loading: savedLoading, saveReport, deleteReport } = useSavedReports();
  const { loading: exportLoading, handleExport } = useFinancialExport();
  
  // Informations de l'entreprise avec fallback
  const organization = orgData ? {
    name: orgData.name,
    address: [orgData.address, orgData.city, orgData.country].filter(Boolean).join(', '),
    registrationNumber: orgData.registrationNumber,
    taxId: orgData.taxId,
    creditScore: dashboardData?.keyPerformanceIndicators?.creditScore ?? 85,
    esgScore: 'N/A', // ESG non disponible dans l'API
    financialRating: dashboardData?.keyPerformanceIndicators?.financialRating ?? 'N/A',
    logo: orgData.logo,
    industry: orgData.industry,
    legalForm: orgData.legalForm,
    phone: orgData.phone,
    email: orgData.email,
    website: orgData.website
  } : {
    name: 'ENTREPRISE ABC SARL',
    address: 'Kinshasa, RD Congo',
    registrationNumber: 'CD/KIN/RCCM/22-B-01234',
    taxId: 'A1234567Y',
    creditScore: dashboardData?.keyPerformanceIndicators?.creditScore ?? 85,
    esgScore: 'N/A',
    financialRating: dashboardData?.keyPerformanceIndicators?.financialRating ?? 'N/A'
  };

  // Sauvegarder automatiquement le rapport généré et ouvrir le modal
  useEffect(() => {
    if (data && user && !loading) {
      const autoSaveReport = async () => {
        const savedReport = await saveReport({
          type: currentConfig.type,
          title: STATEMENT_TYPES[currentConfig.type],
          data,
          currency: currentConfig.currency,
          generatedBy: user.name || user.email || 'N/A',
          organization: {
            name: organization.name,
            registrationNumber: organization.registrationNumber
          },
          isAudited: true,
          format: 'json'
        });
        
        // Ouvrir le modal d'aperçu pour le rapport nouvellement généré
        if (savedReport) {
          setSelectedReport(savedReport);
          setShowPreviewModal(true);
        }
      };
      
      autoSaveReport();
    }
  }, [data, loading]);

  const handleConfigSubmit = async (config: any) => {
    setCurrentConfig({
      type: config.type,
      format: config.format,
      currency: config.currency
    });
    
    await generateReport({
      type: config.type,
      format: config.format,
      date: config.period,
      comparative: config.showComparison,
      currency: config.currency,
      includeNotes: config.showAnalytics
    });

    setShowConfigModal(false);
  };

  const handleViewReport = (report: GeneratedReport) => {
    setSelectedReport(report);
    setCurrentConfig({
      type: report.type,
      format: 'pdf',
      currency: report.currency
    });
    setShowPreviewModal(true);
  };

  const handleDeleteReport = async (reportId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
      const success = await deleteReport(reportId);
      if (success && selectedReport?.id === reportId) {
        setSelectedReport(null);
        setShowPreviewModal(false);
      }
    }
  };

  const handlePrintReport = (report: GeneratedReport) => {
    // Préparer le rapport pour l'impression
    setSelectedReport(report);
    setCurrentConfig({
      type: report.type,
      format: 'pdf',
      currency: report.currency
    });
    
    // Attendre que le state soit mis à jour puis imprimer directement
    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  const handleExportClick = async (format: 'pdf' | 'excel') => {
    if (!data || !user) return;
    
    try {
      await handleExport({
        type: currentConfig.type,
        format,
        data,
        title: STATEMENT_TYPES[currentConfig.type],
        organization,
        generatedBy: user.name || user.email || 'N/A',
        isAudited: true,
        currency: currentConfig.currency
      });
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };  const handlePrint = () => {
    // Référence à l'iframe qui sera utilisée pour l'impression
    const printFrame = document.getElementById('report-print-frame') as HTMLIFrameElement;
    if (!printFrame || !printFrame.contentWindow) return;

    // Accéder au document de l'iframe
    const printDocument = printFrame.contentWindow.document;
    
    // Ouvrir le document pour écriture
    printDocument.open();
    
    // Écrire le contenu HTML d'impression dans l'iframe
    printDocument.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${STATEMENT_TYPES[currentConfig.type]} - Impression</title>
          <meta charset="utf-8">
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #000;
              background: #fff;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
            }
            th {
              background-color: #f8f9fa;
              font-weight: bold;
              text-align: left;
            }
            .text-right {
              text-align: right;
            }
            .bg-gray-700 {
              background-color: #374151;
              color: white;
            }
            .bg-gray-50 {
              background-color: #f9fafb;
            }
            .bg-gray-100 {
              background-color: #f3f4f6;
            }
            .font-bold {
              font-weight: bold;
            }
            .font-medium {
              font-weight: 500;
            }
            h1, h2, h3 {
              color: #111827;
            }
            .text-center {
              text-align: center;
            }
            .mb-8 {
              margin-bottom: 32px;
            }
            .mb-4 {
              margin-bottom: 16px;
            }
            .mb-2 {
              margin-bottom: 8px;
            }
            .mb-1 {
              margin-bottom: 4px;
            }
            .mt-8 {
              margin-top: 32px;
            }
            .mt-4 {
              margin-top: 16px;
            }
            .border-t {
              border-top: 1px solid #e5e7eb;
            }
            .pt-4 {
              padding-top: 16px;
            }
            .text-gray-600 {
              color: #4b5563;
            }
            .text-sm {
              font-size: 0.875rem;
            }
            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div id="print-content"></div>
        </body>
      </html>
    `);
    
    // Fermer le document
    printDocument.close();
    
    // Rendre notre composant React dans l'élément print-content de l'iframe
    if (data && user) {
      const printContainer = printDocument.getElementById('print-content');
      if (printContainer && printFrame.contentWindow) {
        // Injecter le contenu HTML du rapport
        printContainer.innerHTML = document.getElementById('printable-report-content')?.innerHTML || '';
        
        // Attendre que le contenu soit complètement chargé
        setTimeout(() => {
          printFrame.contentWindow?.print();
        }, 500);
      }
    }
  };

  // Données à afficher (rapport sélectionné ou rapport actuel)
  const displayData = selectedReport?.data || data;
  const displayType = selectedReport?.type || currentConfig.type;
  const displayCurrency = selectedReport?.currency || currentConfig.currency;

  // Couleurs des badges selon le type de rapport
  const getReportBadgeVariant = (type: FinancialStatementType): 'primary' | 'success' | 'info' | 'warning' => {
    const variants: Record<string, 'primary' | 'success' | 'info' | 'warning'> = {
      'balance': 'primary',
      'income': 'success',
      'cashflow': 'info',
      'equity-changes': 'warning',
      'notes': 'info',
      'reconciliation': 'primary',
      'analytical': 'warning',
      'social': 'success',
      'statistics': 'info'
    };
    return variants[type] || 'primary';
  };

  return (
    <div className="space-y-6">
      {/* En-tête de la page */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">États Financiers</h1>
        <Button 
          variant="primary" 
          icon={Settings}
          onClick={() => setShowConfigModal(true)}
        >
          Générer un nouveau rapport
        </Button>
      </div>

      {/* Tableau des rapports sauvegardés */}
      <Card title={`Rapports générés (${savedReports.length})`}>
        <Table
          columns={[
            {
              header: 'Type de rapport',
              accessor: (report) => (
                <div className="flex items-center space-x-3">
                  <FileText className="h-4 w-4 text-primary" />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">{STATEMENT_TYPES[report.type]}</span>
                    <Badge variant={getReportBadgeVariant(report.type)} className="mt-1 w-fit">
                      {report.type}
                    </Badge>
                  </div>
                </div>
              ),
            },
            {
              header: 'Date de génération',
              accessor: (report) => new Date(report.generatedAt).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }),
            },
            {
              header: 'Devise',
              accessor: (report) => (
                <Badge variant={report.currency === 'USD' ? 'success' : 'primary'}>
                  {report.currency}
                </Badge>
              ),
              className: 'text-center',
            },
            {
              header: 'Généré par',
              accessor: 'generatedBy',
            },
            {
              header: 'Organisation',
              accessor: (report) => (
                <div className="flex flex-col">
                  <span className="text-sm">{report.organization.name}</span>
                  {report.isAudited && (
                    <Badge variant="success" className="mt-1 w-fit text-xs">
                      Audité
                    </Badge>
                  )}
                </div>
              ),
            },
            {
              header: 'Actions',
              accessor: (report) => (
                <Dropdown
                  items={[
                    {
                      label: 'Voir',
                      icon: Eye,
                      onClick: () => handleViewReport(report),
                      variant: 'primary'
                    },
                    {
                      label: 'Imprimer',
                      icon: Printer,
                      onClick: () => handlePrintReport(report),
                      separator: true
                    },
                    {
                      label: 'Exporter Excel',
                      icon: Download,
                      onClick: async () => {
                        if (!report.data || !user) return;
                        
                        try {
                          await handleExport({
                            type: report.type,
                            format: 'excel',
                            data: report.data,
                            title: STATEMENT_TYPES[report.type],
                            organization,
                            generatedBy: report.generatedBy,
                            isAudited: report.isAudited,
                            currency: report.currency
                          });
                        } catch (error) {
                          console.error('Failed to export:', error);
                          alert('Erreur lors de l\'exportation du rapport');
                        }
                      },
                      variant: 'success'
                    },
                    {
                      label: 'Supprimer',
                      icon: Trash2,
                      onClick: () => handleDeleteReport(report.id),
                      variant: 'danger',
                      separator: true
                    }
                  ]}
                  align="right"
                />
              ),
              className: 'text-center',
              width: '80px',
            },
          ]}
          data={savedReports}
          loading={savedLoading}
          emptyTitle="Aucun rapport généré"
          emptyMessage="Configurez et générez votre premier rapport financier"
          emptyAction={{
            label: 'Générer un rapport',
            onClick: () => setShowConfigModal(true)
          }}
        />
      </Card>

      {/* Modal d'aperçu du rapport */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title={displayData ? STATEMENT_TYPES[displayType] : "Aperçu du rapport"}
        size="2xl"
      >
        {displayData && (
          <div className="space-y-6">
            {/* En-tête du rapport */}
            {user && (
              <ReportHeader
                title={STATEMENT_TYPES[displayType]}
                organization={organization}
                generatedBy={selectedReport?.generatedBy || user.name || user.email || 'N/A'}
                isAudited={selectedReport?.isAudited ?? true}
                currency={displayCurrency}
              />
            )}

            {/* Prévisualisation */}
            <DocumentPreview 
              format={currentConfig.format}
              data={displayData}
              type={displayType}
              loading={loading}
            />

            {/* Message d'erreur */}
            {error && (
              <div className="p-4 bg-red-50 text-red-600 rounded-lg">
                {error}
              </div>
            )}

            {/* Boutons d'action dans le modal */}
            <div className="flex justify-end space-x-3 border-t pt-4 mt-6">
              <Button 
                variant="secondary" 
                icon={Printer}
                onClick={handlePrint}
              >
                Imprimer
              </Button>
              <Button 
                variant="primary" 
                icon={Download}
                onClick={() => handleExportClick('excel')}
                disabled={exportLoading}
              >
                Exporter Excel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de configuration */}
      <ReportConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onSubmit={handleConfigSubmit}
      />

      {/* Zone d'impression cachée */}
      <div className="hidden">
        <div id="printable-report-content">
          {displayData && user && (
            <PrintableReport
              data={displayData}
              type={displayType}
              title={STATEMENT_TYPES[displayType]}
              organization={organization}
              generatedBy={selectedReport?.generatedBy || user.name || user.email || 'N/A'}
              currency={displayCurrency}
              date={new Date().getFullYear().toString()}
            />
          )}
        </div>
        <iframe id="report-print-frame" style={{ display: 'none' }}></iframe>
      </div>
    </div>
  );
}